import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Button, message } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { getReportTemplates } from '../../services/regulatoryReportingService';
import TemplateFormModal from './TemplateFormModal';

const REPORT_TYPE_LABELS = {
  CBN_RETURNS: 'CBN Returns',
  NDIC_RETURNS: 'NDIC Returns',
  AML_CTR: 'AML — CTR',
  AML_SAR: 'AML — SAR',
  AML_STR: 'AML — STR',
  NFIU_REPORT: 'NFIU Report',
  EFCC_REPORT: 'EFCC Compliance',
  CREDIT_BUREAU: 'Credit Bureau',
  CAPITAL_ADEQUACY: 'Capital Adequacy',
  LIQUIDITY_REPORT: 'Liquidity Report',
  PRUDENTIAL_RETURNS: 'Prudential Returns',
  RISK_ASSESSMENT: 'Risk Assessment',
  KYC_COMPLIANCE: 'KYC Compliance',
  TRANSACTION_MONITORING: 'Transaction Monitoring',
  CUSTOM: 'Custom',
};

const APPROVAL_STATUS_CONFIG = {
  APPROVED: { color: 'green', label: 'Approved' },
  PENDING: { color: 'gold', label: 'Pending Approval' },
  REJECTED: { color: 'red', label: 'Rejected' },
  DRAFT: { color: 'default', label: 'Draft' },
};

const TemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [modalVisible, setModalVisible] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  const fetchTemplates = useCallback(async (page = 1, size = 10) => {
    setLoading(true);
    try {
      const res = await getReportTemplates({ page: page - 1, size });
      if (res?.status) {
        setTemplates(res.data?.content || []);
        setPagination({ current: page, pageSize: size, total: res.data?.totalElements || 0 });
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleTableChange = (pag) => fetchTemplates(pag.current, pag.pageSize);

  const handleEdit = (record) => {
    setEditRecord(record);
    setModalVisible(true);
  };

  const handleCreate = () => {
    setEditRecord(null);
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'Template Name',
      dataIndex: 'templateName',
      key: 'templateName',
      render: (name) => (
        <span style={{ fontWeight: 600, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 6 }}>
          <FileTextOutlined style={{ color: '#ff6700' }} />
          {name}
        </span>
      ),
    },
    {
      title: 'Report Type',
      dataIndex: 'reportType',
      key: 'reportType',
      render: (type) => (
        <Tag
          style={{
            background: '#f5f5f5',
            color: '#595959',
            border: '1px solid #e8e8e8',
            borderRadius: 6,
            fontWeight: 500,
          }}
        >
          {REPORT_TYPE_LABELS[type] || type}
        </Tag>
      ),
    },
    {
      title: 'Fields',
      dataIndex: 'fieldMappings',
      key: 'fieldMappings',
      render: (mappings) => (
        <span style={{ color: '#595959', fontSize: 12 }}>
          {Array.isArray(mappings) ? `${mappings.length} field(s)` : '—'}
        </span>
      ),
    },
    {
      title: 'Approval Status',
      dataIndex: 'approvalStatus',
      key: 'approvalStatus',
      render: (status) => {
        const cfg = APPROVAL_STATUS_CONFIG[status] || { color: 'default', label: status || 'N/A' };
        return (
          <Tag color={cfg.color} className="reg-status-tag">
            {cfg.label}
          </Tag>
        );
      },
    },
    {
      title: 'Last Modified',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 170,
      render: (ts) => (
        <span style={{ color: '#8c8c8c', fontSize: 12, fontWeight: 500 }}>
          {ts ? moment(ts).format('MMM DD, YYYY • hh:mm A') : '—'}
        </span>
      ),
    },
    {
      title: 'Modified By',
      dataIndex: 'updatedBy',
      key: 'updatedBy',
      render: (name) => <span style={{ fontWeight: 500 }}>{name || '—'}</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Button
          size="small"
          icon={<EditOutlined />}
          style={{ borderRadius: 6, fontSize: 11 }}
          onClick={() => handleEdit(record)}
        />
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          style={{
            background: '#ff6700',
            borderColor: '#ff6700',
            borderRadius: 8,
            fontWeight: 700,
          }}
        >
          Create Template
        </Button>
      </div>

      <div className="reg-table-card">
        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} templates`,
          }}
          onChange={handleTableChange}
          rowClassName={(_, idx) => (idx % 2 === 0 ? 'reg-row-even' : 'reg-row-odd')}
          className="reg-table"
        />
      </div>

      <TemplateFormModal
        visible={modalVisible}
        onClose={() => { setModalVisible(false); setEditRecord(null); }}
        onSuccess={() => fetchTemplates(pagination.current, pagination.pageSize)}
        editRecord={editRecord}
      />
    </div>
  );
};

export default TemplatesTab;
